import { Fragment, useMemo } from "react";

const KIM_S = "Kim S";
/** Standalone "Kim S" only — not Kim SB, Kim SH, etc. */
const KIM_S_PATTERN = "(?<![A-Za-z0-9])Kim S(?![A-Za-z0-9])";

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

type SegmentKind = "plain" | "kim" | "search" | "kim-search";

interface Segment {
  text: string;
  kind: SegmentKind;
}

interface TextMatch {
  start: number;
  end: number;
  isKim: boolean;
  isSearch: boolean;
}

function collectTextMatches(text: string, searchTerms: string[]): TextMatch[] {
  const matches: TextMatch[] = [];

  const kimRegex = new RegExp(KIM_S_PATTERN, "g");
  let match: RegExpExecArray | null;
  while ((match = kimRegex.exec(text)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      isKim: true,
      isSearch: false,
    });
  }

  for (const term of searchTerms) {
    const searchRegex = new RegExp(escapeRegex(term), "gi");
    while ((match = searchRegex.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        isKim: false,
        isSearch: true,
      });
    }
  }

  matches.sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start),
  );

  const selected: TextMatch[] = [];
  for (const candidate of matches) {
    const overlap = selected.find(
      (picked) => candidate.start < picked.end && candidate.end > picked.start,
    );

    if (!overlap) {
      selected.push({ ...candidate });
      continue;
    }

    if (candidate.start === overlap.start && candidate.end === overlap.end) {
      overlap.isKim ||= candidate.isKim;
      overlap.isSearch ||= candidate.isSearch;
    }
  }

  return selected.sort((a, b) => a.start - b.start);
}

function toSegmentKind(match: TextMatch): SegmentKind {
  if (match.isKim && match.isSearch) return "kim-search";
  if (match.isKim) return "kim";
  if (match.isSearch) return "search";
  return "plain";
}

function segmentPublicationText(text: string, searchQuery: string): Segment[] {
  const searchTerms = searchQuery
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 0);

  const matches = collectTextMatches(text, searchTerms);
  if (matches.length === 0) {
    return [{ text, kind: "plain" }];
  }

  const segments: Segment[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    if (match.start > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.start), kind: "plain" });
    }

    segments.push({
      text: text.slice(match.start, match.end),
      kind: toSegmentKind(match),
    });
    lastIndex = match.end;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), kind: "plain" });
  }

  return segments;
}

function renderSegment(segment: Segment, index: number) {
  switch (segment.kind) {
    case "kim":
      return (
        <strong key={index} className="font-bold text-[#2c4964]">
          {segment.text}
        </strong>
      );
    case "search":
      return (
        <mark
          key={index}
          className="rounded-sm bg-[#fff3cd] px-0.5 text-[#b45309] not-italic"
        >
          {segment.text}
        </mark>
      );
    case "kim-search":
      return (
        <strong key={index} className="font-bold text-[#2c4964]">
          <mark className="rounded-sm bg-[#fff3cd] px-0.5 text-[#b45309] not-italic">
            {segment.text}
          </mark>
        </strong>
      );
    default:
      return <Fragment key={index}>{segment.text}</Fragment>;
  }
}

interface PublicationTextProps {
  text: string;
  searchQuery?: string;
}

export default function PublicationText({ text, searchQuery = "" }: PublicationTextProps) {
  const segments = useMemo(
    () => segmentPublicationText(text, searchQuery),
    [text, searchQuery],
  );

  return <>{segments.map(renderSegment)}</>;
}
